import { FC, useCallback } from 'react';
import { readInlineData, useFragment, useMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import AddIcon from '@mui/icons-material/Add';
import LoadingButton from '@mui/lab/LoadingButton';
import FlightDetails from 'components/FlightDetails';

import { useErrorHandler } from 'lib';

import {
  FlightSearchResult_flight$data,
  FlightSearchResult_flight$key,
} from '__generated__/FlightSearchResult_flight.graphql';
import { FlightSearchResult_makeFlightInput$key } from '__generated__/FlightSearchResult_makeFlightInput.graphql';
import { FlightSearchResultAddMutation } from '__generated__/FlightSearchResultAddMutation.graphql';

type Props = {
  id: string;
  onAdd: () => void;
  flight: FlightSearchResult_flight$key;
};

const makeFlightInput = (flight: FlightSearchResult_flight$data) =>
  readInlineData<FlightSearchResult_makeFlightInput$key>(
    graphql`
      fragment FlightSearchResult_makeFlightInput on FlightJson @inline {
        provider
        supplier
        priceKey
        bookingToken
        priceLive
        priceSupplier

        forwardSegments {
          price
          departDatetime
          arrivalDatetime
          departAirportCode
          arrivalAirportCode
          departCity
          arrivalCity
          supplier
          carrier
          flightNumber
          duration
          direction
        }

        returnSegments {
          price
          departDatetime
          arrivalDatetime
          departAirportCode
          arrivalAirportCode
          departCity
          arrivalCity
          supplier
          carrier
          flightNumber
          duration
          direction
        }
      }
    `,
    flight
  );

const FlightSearchResult: FC<Props> = props => {
  const { id, onAdd } = props;
  const errorHandler = useErrorHandler();

  const flight = useFragment(
    graphql`
      fragment FlightSearchResult_flight on FlightJson {
        ...FlightDetails_flight
        ...FlightSearchResult_makeFlightInput
      }
    `,
    props.flight
  );

  const [addFlight, adding] =
    useMutation<FlightSearchResultAddMutation>(graphql`
      mutation FlightSearchResultAddMutation(
        $first: Int!
        $id: ID!
        $flight: CreateFlightInput!
      ) {
        addFlightToPackageDate(id: $id, flight: $flight) {
          ...PackageEditTabs_package
          ...PackageSidebar_package
        }
      }
    `);

  const handleAddClick = useCallback(() => {
    addFlight({
      variables: {
        id,
        flight: makeFlightInput(flight),
        first: 2147483647,
      },

      ...errorHandler({
        onCompleted: onAdd,
      }),
    });
  }, [addFlight, errorHandler, flight, id, onAdd]);

  return (
    <FlightDetails flight={flight} sx={{ mb: 1 }}>
      <LoadingButton
        loading={adding}
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddClick}>
        Pridėti
      </LoadingButton>
    </FlightDetails>
  );
};

export default FlightSearchResult;
